import type {
  SuccessionCandidateFact,
  SuccessionPlanningProjection,
  SuccessionSlateFact,
} from '../models/SuccessionPlanningPersistence';

/**
 * Carrying a succession draft into a "Save as" chart.
 *
 * Succession drafts are server change-sets scoped to one diagram
 * (`/diagrams/{diagramId}/atlas/succession`), and the fork only copies canvas
 * content — so without this the succession half of a draft is silently left
 * behind. We rebuild the slates from the permissioned projection and replay
 * them into the new diagram's draft.
 *
 * SAFETY: the projection is permissioned. If it withheld anything
 * (`omittedRelationCount`/`omittedPropertyCount`), a replay would write a copy
 * that *looks* complete but is missing restricted data. We refuse to carry in
 * that case rather than produce a quietly lossy succession plan.
 */

export interface SuccessionCarryPlan {
  readonly slates: readonly SuccessionSlateFact[];
  /** True when every slate could be rebuilt in full from what the actor may see. */
  readonly complete: boolean;
  readonly omittedRelationCount: number;
  readonly omittedPropertyCount: number;
}

/** Rebuild slate facts from the permissioned projection read model. */
export function successionCarryPlanFromProjection(
  projection: SuccessionPlanningProjection | undefined | null,
): SuccessionCarryPlan {
  const omittedRelationCount = projection?.authorization.omittedRelationCount ?? 0;
  const omittedPropertyCount = projection?.authorization.omittedPropertyCount ?? 0;
  const complete = omittedRelationCount === 0 && omittedPropertyCount === 0;

  if (!projection || !projection.changeSet) {
    return { slates: [], complete, omittedRelationCount, omittedPropertyCount };
  }

  const keyPositionByTarget = new Map(
    projection.summaries.map((summary) => [summary.targetRef, summary.keyPosition] as const),
  );
  const candidatesByTarget = new Map<string, SuccessionCandidateFact[]>();

  for (const nomination of projection.nominations) {
    // Drop the projection-only fields; keep the candidate fact itself.
    const { targetRef, recordId: _recordId, ...candidate } = nomination;
    const bucket = candidatesByTarget.get(targetRef);
    if (bucket) bucket.push(candidate);
    else candidatesByTarget.set(targetRef, [candidate]);
  }

  const slates: SuccessionSlateFact[] = [];
  const targetRefs = new Set([...candidatesByTarget.keys(), ...keyPositionByTarget.keys()]);
  for (const targetRef of targetRefs) {
    const candidates = candidatesByTarget.get(targetRef) ?? [];
    const keyPosition = keyPositionByTarget.get(targetRef) ?? false;
    // A target with neither candidates nor a key-position flag carries nothing.
    if (candidates.length === 0 && !keyPosition) continue;
    slates.push({
      targetRef,
      keyPosition,
      candidates: candidates.sort((left, right) => (left.rank ?? 0) - (right.rank ?? 0)),
    });
  }

  return { slates, complete, omittedRelationCount, omittedPropertyCount };
}

export interface SuccessionSlatePutResult {
  readonly changeSetId: string;
  readonly scenarioId: string;
}

export interface ReplaySuccessionSlatesInput {
  readonly initialHead?: { changeSetId: string; scenarioId: string };
  readonly slates: readonly SuccessionSlateFact[];
  /** PUTs one slate into the TARGET diagram's succession draft. */
  readonly putSlate: (body: {
    changeSetId?: string;
    scenarioId?: string;
    expectedHeadChangeSetId?: string;
    slate: SuccessionSlateFact;
  }) => Promise<SuccessionSlatePutResult>;
}

export interface ReplaySuccessionSlatesResult {
  readonly carriedCount: number;
  readonly changeSetId?: string;
}

/**
 * Replay slates into a target diagram's succession draft, threading the head
 * forward so each amendment targets the revision the previous one produced.
 */
export async function replaySuccessionSlates(
  input: ReplaySuccessionSlatesInput,
): Promise<ReplaySuccessionSlatesResult> {
  let changeSetId = input.initialHead?.changeSetId;
  let scenarioId = input.initialHead?.scenarioId;
  let carriedCount = 0;

  for (const slate of input.slates) {
    const result = await input.putSlate({
      changeSetId,
      scenarioId,
      expectedHeadChangeSetId: changeSetId,
      slate,
    });
    changeSetId = result.changeSetId;
    scenarioId = result.scenarioId;
    carriedCount += 1;
  }

  return { carriedCount, changeSetId };
}
